// 📄 components/StepCCResources.tsx
import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { GeneralsFormData } from '../../../../../services/commerce/integrationService.ts';

interface StepCCResourcesProps {
  ccResourcePath: string;
  ccResourceEnabled: boolean;
  onChange: (field: keyof GeneralsFormData, value: any) => void;
}

export function StepCCResources({
  ccResourcePath,
  ccResourceEnabled,
  onChange
}: StepCCResourcesProps) {
  const [resources, setResources] = useState([
    { path: '/sap/bc/srt/rfc/sap', type: 'HTTP', active: true },
    { path: '/sap/bc/idoc', type: 'HTTP', active: true },
    { path: 'ARIBACIG', type: 'RFC', active: true }
  ]);

  const [newPath, setNewPath] = useState('');
  const [newType, setNewType] = useState('HTTP');

  const handleAdd = () => {
    if (!newPath.trim()) return;
    setResources([...resources, { path: newPath.trim(), type: newType, active: true }]);
    onChange('ccResourcePath', newPath.trim());
    onChange('ccResourceEnabled', true);
    setNewPath('');
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      <div className="bg-indigo-500/5 border border-indigo-500/10 rounded-xl p-3 space-y-1">
        <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest block">SAP Cloud Connector GUI</span>
        <p className="font-mono text-xs text-gray-300">
          <span>Cloud Connector → Subaccount → Cloud To On-Premise → Access Control → Resources</span>
        </p>
      </div>

      <div>
        <h3 className="text-lg font-medium text-indigo-400">2. Habilitación de Recursos RFC y HTTP</h3>
        <p className="text-sm text-gray-400 mt-1">
          Habilita explícitamente los recursos y servicios Web de tu SAP ERP que Ariba Integration Suite tiene permitido llamar.
          Cualquier ruta o RFC no listada aquí será bloqueada por defecto por el túnel del Cloud Connector.
        </p>
      </div>

      <div className="border border-white/10 rounded-xl overflow-hidden bg-zinc-900/40">
        <div className="flex items-center justify-between bg-zinc-800/80 border-b border-white/10 p-3">
          <span className="text-xs font-semibold text-gray-300 uppercase tracking-wider">Recursos Autorizados en el Access Control</span>
          <span className="text-[10px] font-mono font-bold text-indigo-400">Prefix & Exact Match Rules</span>
        </div>

        <div className="p-3 bg-white/5 border-b border-white/10 flex flex-col sm:flex-row gap-3 items-end">
          <div className="flex-1">
            <label className="block text-[10px] text-gray-400 mb-1">Nombre de Recurso / URL Path</label>
            <input
              type="text"
              value={newPath}
              onChange={(e) => setNewPath(e.target.value)}
              placeholder="ej. /sap/bc/srt/rfc/sap/zsourcing"
              className="w-full bg-zinc-800 border border-white/10 rounded px-2.5 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 font-mono"
            />
          </div>
          <div className="w-full sm:w-32">
            <label className="block text-[10px] text-gray-400 mb-1">Tipo de Recurso</label>
            <select
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
              className="w-full bg-zinc-800 border border-white/10 rounded px-2.5 py-2 text-xs text-white focus:outline-none"
            >
              <option value="HTTP">HTTP Path</option>
              <option value="RFC">RFC Module</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            className="flex items-center justify-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-[#fff] px-4 py-2 rounded text-xs font-semibold"
          >
            <Plus className="w-3.5 h-3.5" /> Agregar
          </button>
        </div>

        <div className="max-h-[160px] overflow-y-auto text-xs font-mono">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-900/60 text-gray-400 text-[10px] uppercase">
                <th className="p-2 border-b border-white/10">Type</th>
                <th className="p-2 border-b border-white/10">Resource Path / Name</th>
                <th className="p-2 border-b border-white/10 text-center">Status</th>
                <th className="p-2 border-b border-white/10 text-center w-12"></th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {resources.map((res, idx) => (
                <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-2 text-indigo-400 font-semibold">{res.type}</td>
                  <td className="p-2 select-all">{res.path}</td>
                  <td className="p-2 text-center">
                    <span className="bg-green-500/10 text-green-400 border border-green-500/20 px-1.5 py-0.5 rounded-full text-[9px] font-bold">
                      Accessible
                    </span>
                  </td>
                  <td className="p-1 text-center">
                    <button
                      type="button"
                      onClick={() => setResources(resources.filter((_, i) => i !== idx))}
                      className="p-1 text-gray-500 hover:text-red-400"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
